import React, { useState } from 'react';
import { Music, Users, Mail, Phone, ChevronRight, Star, MessageCircle, Video, Award, CheckCircle, Menu, X } from 'lucide-react';

// Translation Object
const translations = {
  english: {
    nav: {
      tagline: "Building Confident Worship Team Members",
      books: "Books",
      assessment: "Free Assessment",
      contact: "Contact",
      resources: "Resources",
      getStarted: "Get Started"
    },
    hero: {
      badge: "Empowering Worship Team Members",
      headline: "Lead Worship with",
      headlineConfidence: "Confidence",
      headlineAnd: "and",
      headlinePurpose: "Purpose",
      subheadline: "Start your journey today with our free confidence assessment, then explore our comprehensive resources designed specifically for worship team members like you.",
      check1: "Free Assessment Tool",
      check2: "Bilingual Resources",
      check3: "Music Ministry Coaching",
      painPointsTitle: "Do any of these sound familiar?",
      painPoint1: "You feel nervous before performing, worried about making mistakes",
      painPoint2: "You want to improve your skills but don't know where to start",
      painPoint3: "You're unsure how to balance technical excellence with spiritual authenticity",
      painPoint4: "You wish you had a clear roadmap for growth in worship ministry",
      solutionFearless: "Fearless Worship",
      solutionFearlessText: " provides you with a comprehensive 10-chapter roadmap to overcome performance anxiety and build unshakeable confidence. You'll discover proven strategies for managing nerves, recovering from mistakes, and leading worship with boldness—backed by both spiritual wisdom and practical techniques.",
      solutionIntentional: "Intentional Worship Devotional",
      solutionIntentionalText: " complements this journey perfectly. While Fearless Worship equips you with the tools, this 52-week devotional helps you sustain your growth through daily spiritual formation and weekly service reflections. Together, they create a complete system for transformation in worship ministry.",
      painPointsClosing: "You're not alone. Let's transform your worship ministry together.",
      formTitle: "Start Your Journey Today",
      formSubtitle: "Get instant access to your free confidence assessment",
      nameLabel: "Full Name *",
      namePlaceholder: "Let's start with your full name...",
      emailLabel: "Email Address *",
      emailPlaceholder: "your.email@example.com",
      phoneLabel: "Phone Number (Optional)",
      phonePlaceholder: "(555) 123-4567",
      submitButton: "Get My Free Assessment",
      disclaimer: "By signing up, you'll receive worship leadership tips, resources, and updates. Unsubscribe anytime."
    },
    about: {
      title: "My Journey in Worship Ministry",
      text: "I am José Aponte, a graduate of Musicians Institute (1990). I have spent decades collaborating with worship teams across Puerto Rico and the United States. My musical journey includes touring with renowned ministers such as Marcos Vidal, contributing my gifts and heart to impactful worship experiences. From 2006 to 2007, I served on the faculty of Instituto CanZion de Puerto Rico, where I taught courses in Ensembles, Music Scoring with Finale, Ear Training, Musical Genres, and Electric Bass, shaping the next generation of worship musicians."
    },
    books: {
      title: "Published Resources",
      subtitle: "Practical resources for worship team members in English and Spanish",
      fearlessTitle: "Fearless Worship",
      fearlessSubtitle: "Building Unshakeable Confidence in Music Ministry",
      fearlessDesc: "Overcome performance anxiety, build spiritual confidence, and lead worship with boldness. This comprehensive guide provides practical strategies for worship musicians at every level.",
      fearlessFeature1: "10 chapters of proven confidence-building strategies",
      fearlessFeature2: "Real-world examples and practical exercises",
      fearlessFeature3: "Spiritual and technical preparation guidelines",
      intentionalTitle: "Intentional Worship",
      intentionalSubtitle: "Devotional and Service Journal for Worship Ministry",
      intentionalDesc: "A 52-week journey of spiritual intentionality for those called to lead God's people in worship. Combine daily devotions with practical service reflections to deepen your worship ministry.",
      intentionalFeature1: "52 weeks of guided devotional content",
      intentionalFeature2: "Worship Service journal for intentional development",
      intentionalFeature3: "Spiritual growth and intentional leadership",
      selectLanguage: "Select Language:",
      english: "English",
      spanish: "Español",
      buyNow: "Buy Now on Amazon"
    },
    assessment: {
      title: "Take Your Free Confidence Assessment",
      subtitle: "Discover your strengths, identify growth areas, and receive a personalized action plan designed specifically for your worship ministry journey.",
      stat1: "5 min",
      stat1Label: "Quick Assessment",
      stat2: "4 Areas",
      stat2Label: "Comprehensive Evaluation",
      stat3: "100%",
      stat3Label: "Free",
      button: "Start Free Assessment"
    },
    resources: {
      title: "Free Resources",
      subtitle: "Practical tips and insights for worship team members",
      articleLabel: "ARTICLE",
      articleTitle: "The Two-Second Recovery Rule",
      articleDesc: "What to do when mistakes happen on the platform—and why they don't have to derail your worship leading.",
      articleButton: "Read More",
      sampleLabel: "FREE INTENTIONAL WORSHIP DEVOTIONAL SAMPLE",
      sampleTitle: "8-Week Devotional Sample",
      sampleDesc: "Experience the final 8 weeks of the year with this free sample from Intentional Worship. November & December devotionals included!",
      sampleButton: "Download Free PDF",
      comingSoon: "More resources coming soon!"
    },
    contact: {
      title: "Ready to Transform Your Worship Ministry?",
      subtitle: "Let's connect and discuss how I can help you lead with confidence",
      formTitle: "Send a Message",
      nameLabel: "Name",
      namePlaceholder: "Your name",
      emailLabel: "Email",
      emailPlaceholder: "your@email.com",
      messageLabel: "Message",
      messagePlaceholder: "Tell me about your worship ministry...",
      sendButton: "Send Message",
      getInTouch: "Get In Touch",
      emailTitle: "Email",
      socialTitle: "Connect on Social"
    },
    modals: {
      comingSoonTitle: "Coming Soon!",
      comingSoonText: "This book is currently in the final stages and will be available soon! Sign up below to be notified the moment it launches.",
      comingSoonButton: "Get Notified When It Launches",
      comingSoonLater: "Maybe Later",
      assessmentTitle: "Discover Your Worship Leadership Strengths!",
      assessmentSubtitle: "In just 5 minutes, get a personalized confidence plan designed specifically for your worship ministry journey.",
      assessmentReceive: "You'll Receive:",
      assessmentItem1: "Personalized Action Plan",
      assessmentItem1Text: " to overcome performance anxiety",
      assessmentItem2: "Confidence Score",
      assessmentItem2Text: " across 4 key worship areas",
      assessmentItem3: "Practical Strategies",
      assessmentItem3Text: " you can implement immediately",
      assessmentItem4: "Custom Recommendations",
      assessmentItem4Text: " based on your unique needs",
      assessmentStat1: "5 min",
      assessmentStat1Label: "Quick & Easy",
      assessmentStat2: "Personalized",
      assessmentStat2Label: "Just For You",
      assessmentStat3: "100%",
      assessmentStat3Label: "Free",
      assessmentYes: "Yes! Get My Free Assessment",
      assessmentLater: "Maybe Later",
      emailConfirmTitle: "Check Your Email!",
      emailConfirmText: "Thank you for signing up! We've sent an email to",
      emailConfirmText2: "with your free assessment link. Please check your inbox (and spam folder) in the next few minutes.",
      emailConfirmButton: "Got It!",
      contactSuccessTitle: "Message Sent!",
      contactSuccessText: "Thank you! Your message has been sent. We'll get back to you soon.",
      contactSuccessButton: "Got It!"
    },
    footer: {
      tagline: "Equipping worship team members to lead with confidence and purpose.",
      booksTitle: "Books",
      fearlessWorship: "Fearless Worship",
      intentionalWorship: "Intentional Worship Journal",
      spanishEditions: "Spanish Editions",
      resourcesTitle: "Resources",
      freeAssessment: "Free Assessment",
      freeResources: "Free Resources",
      contactTitle: "Contact",
      getInTouch: "Get In Touch",
      emailUs: "Email Us",
      copyright: "© 2025 Intentional Worship Life. All rights reserved."
    }
  },
  spanish: {
    nav: {
      tagline: "Formando Miembros Seguros del Equipo de Alabanza",
      books: "Libros",
      assessment: "Evaluación Gratuita",
      contact: "Contacto",
      resources: "Recursos",
      getStarted: "Comenzar"
    },
    hero: {
      badge: "Empoderando a los Miembros del Equipo de Alabanza",
      headline: "Lidera la Alabanza con",
      headlineConfidence: "Confianza",
      headlineAnd: "y",
      headlinePurpose: "Propósito",
      subheadline: "Comienza tu jornada hoy con nuestra evaluación de confianza gratuita, luego explora nuestros recursos integrales diseñados específicamente para miembros del equipo de alabanza como tú.",
      check1: "Herramienta de Evaluación Gratuita",
      check2: "Recursos Bilingües",
      check3: "Mentoría en Ministerio Musical",
      painPointsTitle: "¿Alguno de estos te suena familiar?",
      painPoint1: "Te sientes nervioso antes de presentarte, preocupado por cometer errores",
      painPoint2: "Quieres mejorar tus habilidades pero no sabes por dónde empezar",
      painPoint3: "No estás seguro de cómo equilibrar la excelencia técnica con la autenticidad espiritual",
      painPoint4: "Deseas tener una hoja de ruta clara para el crecimiento en el ministerio de alabanza",
      solutionFearless: "Adoración Sin Miedo",
      solutionFearlessText: " te proporciona una hoja de ruta integral de 10 capítulos para superar la ansiedad de rendimiento y construir una confianza inquebrantable. Descubrirás estrategias probadas para manejar los nervios, recuperarte de los errores y liderar la alabanza con valentía, respaldadas tanto por sabiduría espiritual como por técnicas prácticas.",
      solutionIntentional: "Devocional Adoración Intencional",
      solutionIntentionalText: " complementa este viaje perfectamente. Mientras que Adoración Sin Miedo te equipa con las herramientas, este devocional de 52 semanas te ayuda a sostener tu crecimiento a través de la formación espiritual diaria y reflexiones semanales de servicio. Juntos, crean un sistema completo para la transformación en el ministerio de alabanza.",
      painPointsClosing: "No estás solo. Transformemos tu ministerio de alabanza juntos.",
      formTitle: "Comienza Tu Jornada Hoy",
      formSubtitle: "Obtén acceso instantáneo a tu evaluación de confianza gratuita",
      nameLabel: "Nombre Completo *",
      namePlaceholder: "Comencemos con tu nombre completo...",
      emailLabel: "Correo Electrónico *",
      emailPlaceholder: "tu.correo@ejemplo.com",
      phoneLabel: "Número de Teléfono (Opcional)",
      phonePlaceholder: "(555) 123-4567",
      submitButton: "Obtener Mi Evaluación Gratuita",
      disclaimer: "Al registrarte, recibirás consejos de liderazgo de alabanza, recursos y actualizaciones. Cancela tu suscripción en cualquier momento."
    },
    about: {
      title: "Mi Jornada en el Ministerio de Alabanza",
      text: "Soy José Aponte, graduado del Instituto de Músicos (1990). He pasado décadas colaborando con equipos de alabanza en Puerto Rico y los Estados Unidos. Mi trayectoria musical incluye giras con ministros reconocidos como Marcos Vidal, contribuyendo mis dones y corazón a experiencias impactantes de alabanza. De 2006 a 2007, serví en la facultad del Instituto CanZion de Puerto Rico, donde enseñé cursos de Ensambles, Notación Musical con Finale, Entrenamiento Auditivo, Géneros Musicales y Bajo Eléctrico, formando la próxima generación de músicos de alabanza."
    },
    books: {
      title: "Recursos Publicados",
      subtitle: "Recursos prácticos para miembros del equipo de alabanza en inglés y español",
      fearlessTitle: "Adoración Sin Miedo",
      fearlessSubtitle: "Construyendo Confianza Inquebrantable en el Ministerio Musical",
      fearlessDesc: "Supera la ansiedad de rendimiento, construye confianza espiritual y lidera la alabanza con valentía. Esta guía integral proporciona estrategias prácticas para músicos de alabanza en todos los niveles.",
      fearlessFeature1: "10 capítulos de estrategias probadas para construir confianza",
      fearlessFeature2: "Ejemplos del mundo real y ejercicios prácticos",
      fearlessFeature3: "Pautas de preparación espiritual y técnica",
      intentionalTitle: "Adoración Intencional",
      intentionalSubtitle: "Devocional y Diario de Servicio para el Ministerio de Alabanza",
      intentionalDesc: "Un viaje de 52 semanas de intencionalidad espiritual para aquellos llamados a guiar al pueblo de Dios en alabanza. Combina devocionales diarios con reflexiones prácticas de servicio para profundizar tu ministerio de alabanza.",
      intentionalFeature1: "52 semanas de contenido devocional guiado",
      intentionalFeature2: "Diario de servicio de alabanza para desarrollo intencional",
      intentionalFeature3: "Crecimiento espiritual y liderazgo intencional",
      selectLanguage: "Seleccionar Idioma:",
      english: "English",
      spanish: "Español",
      buyNow: "Comprar Ahora en Amazon"
    },
    assessment: {
      title: "Realiza Tu Evaluación de Confianza Gratuita",
      subtitle: "Descubre tus fortalezas, identifica áreas de crecimiento y recibe un plan de acción personalizado diseñado específicamente para tu jornada en el ministerio de alabanza.",
      stat1: "5 min",
      stat1Label: "Evaluación Rápida",
      stat2: "4 Áreas",
      stat2Label: "Evaluación Integral",
      stat3: "100%",
      stat3Label: "Gratis",
      button: "Comenzar Evaluación Gratuita"
    },
    resources: {
      title: "Recursos Gratuitos",
      subtitle: "Consejos prácticos e ideas para miembros del equipo de alabanza",
      articleLabel: "ARTÍCULO",
      articleTitle: "La Regla de Recuperación de Dos Segundos",
      articleDesc: "Qué hacer cuando ocurren errores en la plataforma—y por qué no tienen que descarrilar tu liderazgo de alabanza.",
      articleButton: "Leer Más",
      sampleLabel: "MUESTRA GRATUITA DEL DEVOCIONAL ADORACIÓN INTENCIONAL",
      sampleTitle: "Muestra de Devocional de 8 Semanas",
      sampleDesc: "Experimenta las últimas 8 semanas del año con esta muestra gratuita de Adoración Intencional. ¡Devocionales de noviembre y diciembre incluidos!",
      sampleButton: "Descargar PDF Gratis",
      comingSoon: "¡Más recursos próximamente!"
    },
    contact: {
      title: "¿Listo para Transformar Tu Ministerio de Alabanza?",
      subtitle: "Conectémonos y hablemos sobre cómo puedo ayudarte a liderar con confianza",
      formTitle: "Enviar un Mensaje",
      nameLabel: "Nombre",
      namePlaceholder: "Tu nombre",
      emailLabel: "Correo Electrónico",
      emailPlaceholder: "tu@correo.com",
      messageLabel: "Mensaje",
      messagePlaceholder: "Cuéntame sobre tu ministerio de alabanza...",
      sendButton: "Enviar Mensaje",
      getInTouch: "Ponte en Contacto",
      emailTitle: "Correo Electrónico",
      socialTitle: "Conéctate en Redes Sociales"
    },
    modals: {
      comingSoonTitle: "¡Próximamente!",
      comingSoonText: "¡Este libro está actualmente en las etapas finales y estará disponible pronto! Regístrate a continuación para ser notificado en el momento en que se lance.",
      comingSoonButton: "Notificarme Cuando Se Lance",
      comingSoonLater: "Tal Vez Más Tarde",
      assessmentTitle: "¡Descubre Tus Fortalezas de Liderazgo de Alabanza!",
      assessmentSubtitle: "En solo 5 minutos, obtén un plan de confianza personalizado diseñado específicamente para tu jornada en el ministerio de alabanza.",
      assessmentReceive: "Recibirás:",
      assessmentItem1: "Plan de Acción Personalizado",
      assessmentItem1Text: " para superar la ansiedad de rendimiento",
      assessmentItem2: "Puntuación de Confianza",
      assessmentItem2Text: " en 4 áreas clave de alabanza",
      assessmentItem3: "Estrategias Prácticas",
      assessmentItem3Text: " que puedes implementar inmediatamente",
      assessmentItem4: "Recomendaciones Personalizadas",
      assessmentItem4Text: " basadas en tus necesidades únicas",
      assessmentStat1: "5 min",
      assessmentStat1Label: "Rápido y Fácil",
      assessmentStat2: "Personalizado",
      assessmentStat2Label: "Solo Para Ti",
      assessmentStat3: "100%",
      assessmentStat3Label: "Gratis",
      assessmentYes: "¡Sí! Obtener Mi Evaluación Gratuita",
      assessmentLater: "Tal Vez Más Tarde",
      emailConfirmTitle: "¡Revisa Tu Correo Electrónico!",
      emailConfirmText: "¡Gracias por registrarte! Hemos enviado un correo electrónico a",
      emailConfirmText2: "con tu enlace de evaluación gratuita. Por favor revisa tu bandeja de entrada (y carpeta de spam) en los próximos minutos.",
      emailConfirmButton: "¡Entendido!",
      contactSuccessTitle: "¡Mensaje Enviado!",
      contactSuccessText: "¡Gracias! Tu mensaje ha sido enviado. Te responderemos pronto.",
      contactSuccessButton: "¡Entendido!"
    },
    footer: {
      tagline: "Equipando a los miembros del equipo de alabanza para liderar con confianza y propósito.",
      booksTitle: "Libros",
      fearlessWorship: "Adoración Sin Miedo",
      intentionalWorship: "Diario de Adoración Intencional",
      spanishEditions: "Ediciones en Español",
      resourcesTitle: "Recursos",
      freeAssessment: "Evaluación Gratuita",
      freeResources: "Recursos Gratuitos",
      contactTitle: "Contacto",
      getInTouch: "Ponte en Contacto",
      emailUs: "Envíanos un Correo",
      copyright: "© 2025 Intentional Worship Life. Todos los derechos reservados."
    }
  }
};

export default function IntentionalWorshipLanding() {
  const [language, setLanguage] = useState('english');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: 'assessment'
  });
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalBook, setModalBook] = useState('');
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [showAssessmentFormModal, setShowAssessmentFormModal] = useState(false);
  const [intentionalWorshipLanguage, setIntentionalWorshipLanguage] = useState('english');
  const [fearlessWorshipLanguage, setFearlessWorshipLanguage] = useState('english');
  const [showEmailConfirmationModal, setShowEmailConfirmationModal] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [showContactSuccessModal, setShowContactSuccessModal] = useState(false);

  const t = translations[language];

  // Initialize language from URL params or localStorage on mount
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    
    if (langParam === 'es' || langParam === 'spanish') {
      setLanguage('spanish');
    } else if (langParam === 'en' || langParam === 'english') {
      setLanguage('english');
    } else {
      // Check localStorage if no URL param
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage === 'spanish' || savedLanguage === 'english') {
        setLanguage(savedLanguage);
      }
    }
  }, []);

  // Save language to localStorage when it changes
  React.useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Sync book language selectors with main language toggle
  React.useEffect(() => {
    setIntentionalWorshipLanguage(language);
    setFearlessWorshipLanguage(language);
  }, [language]);

  // Handle hash scrolling after language is set
  React.useEffect(() => {
    // Small delay to ensure the page has rendered with the correct language
    const timeoutId = setTimeout(() => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [language]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const form = e.target;
    const emailValue = formData.email;
    
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(new FormData(form)).toString()
    })
    .then(() => {
      setSubmittedEmail(emailValue);
      setShowAssessmentFormModal(false);
      setShowEmailConfirmationModal(true);
    })
    .catch((error) => {
      alert('Oops! Something went wrong. Please try again.');
      console.error(error);
    });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    
    const form = e.target;
    
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(new FormData(form)).toString()
    })
    .then(() => {
      setShowContactSuccessModal(true);
    })
    .catch((error) => {
      alert('Oops! Something went wrong. Please try again.');
      console.error(error);
    });
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setShowMobileMenu(false);
  };

  const openModal = (bookName) => {
    setModalBook(bookName);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalBook('');
  };

  const handleGetNotified = () => {
    closeModal();
    scrollToSection('contact');
  };

  const openAssessmentModal = () => {
    setShowAssessmentModal(true);
  };

  const closeAssessmentModal = () => {
    setShowAssessmentModal(false);
  };

  const handleStartAssessment = () => {
    closeAssessmentModal();
    setShowAssessmentFormModal(true);
  };

  const openAssessmentFormModal = () => {
    setShowAssessmentFormModal(true);
  };

  const closeAssessmentFormModal = () => {
    setShowAssessmentFormModal(false);
  };

  const closeEmailConfirmationModal = () => {
    setShowEmailConfirmationModal(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      interest: 'assessment'
    });
    setSubmittedEmail('');
  };

  const closeContactSuccessModal = () => {
    setShowContactSuccessModal(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      interest: 'assessment'
    });
  };

  const handleIntentionalWorshipBuyNow = () => {
    if (intentionalWorshipLanguage === 'english') {
      window.open('https://a.co/d/51rhLRi', '_blank');
    } else {
      window.open('https://a.co/d/6akKX0X', '_blank');
    }
  };

  const handleFearlessWorshipBuyNow = () => {
    if (fearlessWorshipLanguage === 'english') {
      window.open('https://a.co/d/eicmuGM', '_blank');
    } else {
      window.open('https://a.co/d/dGvgtSl', '_blank');
    }
  };

  const handleSpanishEditionsClick = () => {
    setFearlessWorshipLanguage('spanish');
    setIntentionalWorshipLanguage('spanish');
    scrollToSection('books');
  };

  const handleEnglishEditionsClick = () => {
    setFearlessWorshipLanguage('english');
    setIntentionalWorshipLanguage('english');
    scrollToSection('books');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <span className="text-4xl">🙏</span>
              <div>
                <div className="text-xl font-bold text-gray-900">Intentional Worship Life</div>
                <div className="text-xs text-gray-500">{t.nav.tagline}</div>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('books')} className="text-gray-700 hover:text-amber-600 transition">{t.nav.books}</button>
              <button onClick={() => scrollToSection('assessment')} className="text-gray-700 hover:text-amber-600 transition">{t.nav.assessment}</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-amber-600 transition">{t.nav.contact}</button>
              <button onClick={() => scrollToSection('resources')} className="text-gray-700 hover:text-amber-600 transition">{t.nav.resources}</button>
              <button 
                onClick={() => setLanguage(language === 'english' ? 'spanish' : 'english')}
                className="text-gray-700 hover:text-amber-600 transition font-semibold"
              >
                {language === 'english' ? 'Español' : 'English'}
              </button>
              <button onClick={openAssessmentModal} className="bg-amber-600 text-white px-6 py-2 rounded-lg hover:bg-amber-700 transition">
                {t.nav.getStarted}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden text-gray-700"
            >
              {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('books')} className="block w-full text-left text-gray-700 hover:text-amber-600 py-2">{t.nav.books}</button>
              <button onClick={() => scrollToSection('assessment')} className="block w-full text-left text-gray-700 hover:text-amber-600 py-2">{t.nav.assessment}</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-700 hover:text-amber-600 py-2">{t.nav.contact}</button>
              <button onClick={() => scrollToSection('resources')} className="block w-full text-left text-gray-700 hover:text-amber-600 py-2">{t.nav.resources}</button>
              <button 
                onClick={() => { setLanguage(language === 'english' ? 'spanish' : 'english'); setShowMobileMenu(false); }}
                className="block w-full text-left text-gray-700 hover:text-amber-600 py-2 font-semibold"
              >
                {language === 'english' ? 'Español' : 'English'}
              </button>
              <button onClick={() => { openAssessmentModal(); setShowMobileMenu(false); }} className="block w-full text-left bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700">{t.nav.getStarted}</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section with Lead Capture */}
      <section className="relative bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gray-700 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gray-600 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Pain Points Section - Centered at Top */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-white rounded-xl p-8 border border-gray-200 text-center shadow-lg">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-amber-600">{t.hero.painPointsTitle}</h2>
              <ul className="space-y-3 text-gray-700 text-left max-w-2xl mx-auto mb-6">
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>{t.hero.painPoint1}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>{t.hero.painPoint2}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>{t.hero.painPoint3}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>{t.hero.painPoint4}</span>
                </li>
              </ul>
              
              {/* Solution - Books Overview */}
              <div className="space-y-3 text-gray-700 text-left max-w-2xl mx-auto mb-6">
                <p className="flex items-start gap-3">
                  <span className="text-amber-600 mt-1">•</span>
                  <span><span className="font-bold text-amber-600">{t.hero.solutionFearless}</span>{t.hero.solutionFearlessText}</span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-amber-600 mt-1">•</span>
                  <span>{t.hero.solutionIntentional}{t.hero.solutionIntentionalText}</span>
                </p>
              </div>

              <p className="text-xl font-semibold text-gray-900">
                {t.hero.painPointsClosing}
              </p>
            </div>
          </div>

          {/* Elegant Divider */}
          <div className="max-w-3xl mx-auto mb-16">
            <div className="h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent opacity-50"></div>
          </div>

          {/* Main Hero Content Grid */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-gray-700 bg-opacity-60 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                {t.hero.badge}
              </div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {t.hero.headline} <span className="text-amber-400">{t.hero.headlineConfidence}</span> {t.hero.headlineAnd} <span className="text-amber-400">{t.hero.headlinePurpose}</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                {t.hero.subheadline}
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-400" />
                  <span>{t.hero.check1}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-400" />
                  <span>{t.hero.check2}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-400" />
                  <span>{t.hero.check3}</span>
                </div>
              </div>
            </div>

            {/* Lead Capture Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 text-gray-900">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.hero.formTitle}</h3>
                <p className="text-gray-600">{t.hero.formSubtitle}</p>
              </div>

              <form 
                name="hero-contact" 
                method="POST" 
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit} 
                className="space-y-4"
              >
                  <input type="hidden" name="form-name" value="hero-contact" />
                  <p className="hidden">
                    <label>Don't fill this out if you're human: <input name="bot-field" /></label>
                  </p>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.hero.nameLabel}</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder={t.hero.namePlaceholder}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.hero.emailLabel}</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder={t.hero.emailPlaceholder}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t.hero.phoneLabel}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      placeholder={t.hero.phonePlaceholder}
                    />
                  </div>

                  <div data-netlify-recaptcha="true"></div>

                  <button
                    type="submit"
                    className="w-full bg-amber-600 text-white py-4 rounded-lg font-semibold hover:bg-amber-700 transition transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                  >
                    {t.hero.submitButton}
                    <ChevronRight className="w-5 h-5" />
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    {t.hero.disclaimer}
                  </p>
                </form>
            </div>
          </div>
        </div>
      </section>

      {/* About Me Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.about.title}</h2>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="md:flex">
              {/* Photo */}
              <div className="md:w-1/3 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center p-8">
                <img 
                  src="/jose-aponte-photo.png" 
                  alt="José Aponte" 
                  className="rounded-xl shadow-lg w-full max-w-xs object-cover"
                />
              </div>
              
              {/* Text Content */}
              <div className="md:w-2/3 p-8 md:p-12 flex items-center">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t.about.text}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Books Section */}
      <section id="books" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.books.title}</h2>
            <p className="text-xl text-gray-600">{t.books.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Fearless Worship */}
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl shadow-xl overflow-hidden border border-orange-100">
              <div className="bg-gradient-to-br from-amber-600 to-orange-600 text-white p-6 text-center">
                <img src={fearlessWorshipLanguage === 'spanish' ? '/adoracion-sin-miedo.jpg' : '/fearless-worship-cover.png'} alt="Fearless Worship" className="w-56 h-80 mx-auto mb-3 object-contain rounded-lg shadow-xl" />
                <h3 className="text-2xl font-bold">{t.books.fearlessTitle}</h3>
                <p className="text-orange-100 mt-2">{t.books.fearlessSubtitle}</p>
              </div>
              <div className="p-8">
                <div className="mb-6">
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {t.books.fearlessDesc}
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{t.books.fearlessFeature1}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{t.books.fearlessFeature2}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{t.books.fearlessFeature3}</span>
                    </li>
                  </ul>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2 font-semibold">{t.books.selectLanguage}</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setFearlessWorshipLanguage('english')}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer ${
                        fearlessWorshipLanguage === 'english'
                          ? 'bg-amber-600 text-white shadow-md'
                          : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                      }`}
                    >
                      {t.books.english}
                    </button>
                    <button
                      onClick={() => setFearlessWorshipLanguage('spanish')}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer ${
                        fearlessWorshipLanguage === 'spanish'
                          ? 'bg-orange-600 text-white shadow-md'
                          : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                      }`}
                    >
                      {t.books.spanish}
                    </button>
                  </div>
                </div>
                <button 
                  onClick={handleFearlessWorshipBuyNow}
                  className="w-full bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 transition"
                >
                  {t.books.buyNow}
                </button>
              </div>
            </div>

            {/* Intentional Worship Devotional-Journal */}
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl shadow-xl overflow-hidden border border-orange-100">
              <div className="bg-gradient-to-br from-orange-600 to-orange-700 text-white p-6 text-center">
                <img src={intentionalWorshipLanguage === 'spanish' ? '/adoracion-intencional.jpg' : '/intentional-worship-cover.png'} alt="Intentional Worship Journal" className="w-56 h-80 mx-auto mb-3 object-contain rounded-lg shadow-xl" />
                <h3 className="text-2xl font-bold">{t.books.intentionalTitle}</h3>
                <p className="text-orange-100 mt-2">{t.books.intentionalSubtitle}</p>
              </div>
              <div className="p-8">
                <div className="mb-6">
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {t.books.intentionalDesc}
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{t.books.intentionalFeature1}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{t.books.intentionalFeature2}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{t.books.intentionalFeature3}</span>
                    </li>
                  </ul>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2 font-semibold">{t.books.selectLanguage}</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setIntentionalWorshipLanguage('english')}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer ${
                        intentionalWorshipLanguage === 'english'
                          ? 'bg-amber-600 text-white shadow-md'
                          : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                      }`}
                    >
                      {t.books.english}
                    </button>
                    <button
                      onClick={() => setIntentionalWorshipLanguage('spanish')}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer ${
                        intentionalWorshipLanguage === 'spanish'
                          ? 'bg-orange-600 text-white shadow-md'
                          : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                      }`}
                    >
                      {t.books.spanish}
                    </button>
                  </div>
                </div>
                <button 
                  onClick={handleIntentionalWorshipBuyNow}
                  className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition"
                >
                  {t.books.buyNow}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Assessment CTA */}
      <section id="assessment" className="py-20 bg-gradient-to-br from-amber-600 to-orange-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-20 h-20 mx-auto mb-6 text-orange-200" />
          <h2 className="text-4xl font-bold mb-6">{t.assessment.title}</h2>
          <p className="text-xl text-orange-50 mb-8 leading-relaxed">
            {t.assessment.subtitle}
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white bg-opacity-10 backdrop-blur rounded-lg p-6">
              <div className="text-3xl font-bold text-orange-200 mb-2">{t.assessment.stat1}</div>
              <div className="text-orange-50">{t.assessment.stat1Label}</div>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur rounded-lg p-6">
              <div className="text-3xl font-bold text-orange-200 mb-2">{t.assessment.stat2}</div>
              <div className="text-orange-50">{t.assessment.stat2Label}</div>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur rounded-lg p-6">
              <div className="text-3xl font-bold text-orange-200 mb-2">{t.assessment.stat3}</div>
              <div className="text-orange-50">{t.assessment.stat3Label}</div>
            </div>
          </div>

          <button
            onClick={openAssessmentFormModal}
            className="bg-orange-800 bg-opacity-70 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition transform hover:scale-105 shadow-xl inline-flex items-center gap-2"
          >
            {t.assessment.button}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Resources/Blog Preview */}
      <section id="resources" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.resources.title}</h2>
            <p className="text-xl text-gray-600">{t.resources.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
            {/* Article */}
            <div className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition">
              <div className="text-sm text-amber-600 font-semibold mb-3">{t.resources.articleLabel}</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.resources.articleTitle}</h3>
              <p className="text-gray-600 mb-6">{t.resources.articleDesc}</p>
              <a 
                href={language === 'spanish' ? '/spanish-article.html' : '/two-second-recovery-article.html'} 
                className="text-amber-600 font-semibold hover:text-amber-700 flex items-center gap-1"
              >
                {t.resources.articleButton} <ChevronRight className="w-5 h-5" />
              </a>
            </div>

            {/* 8-Week Devotional Sample */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-8 hover:shadow-lg transition border-2 border-amber-200">
              <div className="text-sm text-orange-600 font-semibold mb-3">{t.resources.sampleLabel}</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.resources.sampleTitle}</h3>
              <p className="text-gray-600 mb-6">{t.resources.sampleDesc}</p>
              <a 
                href={language === 'spanish' ? '/Adoracion-Intencional-Muestra-8-Semanas.pdf' : '/Intentional-Worship-8-Week-Sample.pdf'} 
                download
                className="inline-flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition"
              >
                {t.resources.sampleButton} <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-500 italic">{t.resources.comingSoon}</p>
          </div>
        </div>
      </section>

      {/* Contact/Final CTA */}
      <section id="contact" className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{t.contact.title}</h2>
            <p className="text-xl text-gray-300">{t.contact.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="bg-white bg-opacity-10 backdrop-blur rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6">{t.contact.formTitle}</h3>
              <form 
                name="contact" 
                method="POST" 
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleContactSubmit} 
                className="space-y-4"
              >
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden">
                  <label>Don't fill this out if you're human: <input name="bot-field" /></label>
                </p>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">{t.contact.nameLabel}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-orange-500"
                    placeholder={t.contact.namePlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">{t.contact.emailLabel}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-orange-500"
                    placeholder={t.contact.emailPlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">{t.contact.messageLabel}</label>
                  <textarea
                    name="message"
                    rows="4"
                    required
                    className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-orange-500"
                    placeholder={t.contact.messagePlaceholder}
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition"
                >
                  {t.contact.sendButton}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <div className="bg-white bg-opacity-10 backdrop-blur rounded-xl p-8 mb-6">
                <h3 className="text-2xl font-bold mb-6">{t.contact.getInTouch}</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Mail className="w-6 h-6 text-orange-300 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold mb-1">{t.contact.emailTitle}</div>
                      <a href="mailto:info@intentionalworshiplife.com" className="text-gray-300 hover:text-white">
                        info@intentionalworshiplife.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white bg-opacity-10 backdrop-blur rounded-xl p-8">
                <h3 className="text-xl font-bold mb-4">{t.contact.socialTitle}</h3>
                <div className="flex gap-4">
                  <a href="https://www.facebook.com/joseapontebass" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition">
                    <img src="/facebook-icon.png" alt="Facebook" className="w-8 h-8" />
                  </a>
                  <a href="https://www.instagram.com/joseapontebass" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition">
                    <img src="/instagram-icon.png" alt="Instagram" className="w-8 h-8" />
                  </a>
                  <a href="https://www.tiktok.com/@joseapontebass" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition">
                    <img src="/tiktok-icon.webp" alt="TikTok" className="w-8 h-8" />
                  </a>
                  <a href="https://www.youtube.com/@joseapontebass" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition">
                    <img src="/youtube-icon.jpg" alt="YouTube" className="w-8 h-8" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-10 h-10 text-amber-600" />
              </div>
              
              <h3 className="text-3xl font-bold text-gray-900 mb-3">{t.modals.comingSoonTitle}</h3>
              
              <p className="text-xl text-gray-700 mb-4 font-semibold">{modalBook}</p>
              
              <p className="text-gray-600 mb-8 leading-relaxed">
                {t.modals.comingSoonText}
              </p>

              <div className="space-y-3">
                <button
                  onClick={handleGetNotified}
                  className="w-full bg-amber-600 text-white py-4 rounded-lg font-semibold hover:bg-amber-700 transition transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                >
                  {t.modals.comingSoonButton}
                  <ChevronRight className="w-5 h-5" />
                </button>
                
                <button
                  onClick={closeModal}
                  className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition"
                >
                  {t.modals.comingSoonLater}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Encouragement Modal */}
      {showAssessmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={closeAssessmentModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={closeAssessmentModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-10 h-10 text-amber-600" />
              </div>
              
              <h3 className="text-3xl font-bold text-gray-900 mb-3">{t.modals.assessmentTitle}</h3>
              
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {t.modals.assessmentSubtitle}
              </p>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 mb-6 text-left">
                <h4 className="font-bold text-gray-900 mb-3 text-center">{t.modals.assessmentReceive}</h4>
                <ul className="space-y-2.5 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>{t.modals.assessmentItem1}</strong>{t.modals.assessmentItem1Text}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>{t.modals.assessmentItem2}</strong>{t.modals.assessmentItem2Text}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>{t.modals.assessmentItem3}</strong>{t.modals.assessmentItem3Text}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>{t.modals.assessmentItem4}</strong>{t.modals.assessmentItem4Text}</span>
                  </li>
                </ul>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-xl font-bold text-amber-600">{t.modals.assessmentStat1}</div>
                  <div className="text-xs text-gray-600">{t.modals.assessmentStat1Label}</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-lg font-bold text-amber-600 leading-tight">{t.modals.assessmentStat2}</div>
                  <div className="text-xs text-gray-600">{t.modals.assessmentStat2Label}</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="text-xl font-bold text-amber-600">{t.modals.assessmentStat3}</div>
                  <div className="text-xs text-gray-600">{t.modals.assessmentStat3Label}</div>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleStartAssessment}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-4 rounded-lg font-bold text-lg hover:from-amber-700 hover:to-orange-700 transition transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                >
                  {t.modals.assessmentYes}
                  <ChevronRight className="w-5 h-5" />
                </button>
                
                <button
                  onClick={closeAssessmentModal}
                  className="w-full bg-gray-100 text-gray-600 py-3 rounded-lg font-semibold hover:bg-gray-200 transition text-sm"
                >
                  {t.modals.assessmentLater}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Form Modal */}
      {showAssessmentFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={closeAssessmentFormModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={closeAssessmentFormModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t.hero.formTitle}</h3>
              <p className="text-gray-600">{t.hero.formSubtitle}</p>
            </div>

            <form 
              name="assessment-modal-form" 
              method="POST" 
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit} 
              className="space-y-4"
            >
                <input type="hidden" name="form-name" value="assessment-modal-form" />
                <p className="hidden">
                  <label>Don't fill this out if you're human: <input name="bot-field" /></label>
                </p>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t.hero.nameLabel}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder={t.hero.namePlaceholder}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t.hero.emailLabel}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder={t.hero.emailPlaceholder}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t.hero.phoneLabel}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder={t.hero.phonePlaceholder}
                  />
                </div>

                <div data-netlify-recaptcha="true"></div>

                <button 
                  type="submit"
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-4 rounded-lg font-bold text-lg hover:from-amber-700 hover:to-orange-700 transition shadow-lg flex items-center justify-center gap-2"
                >
                  {t.hero.submitButton}
                  <ChevronRight className="w-5 h-5" />
                </button>

                <p className="text-xs text-gray-500 text-center">
                  {t.hero.disclaimer}
                </p>
              </form>
          </div>
        </div>
      )}

      {/* Email Confirmation Modal */}
      {showEmailConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={closeEmailConfirmationModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={closeEmailConfirmationModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.modals.emailConfirmTitle}</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                {t.modals.emailConfirmText} <span className="font-semibold text-amber-600">{submittedEmail}</span> {t.modals.emailConfirmText2}
              </p>
              
              <button
                onClick={closeEmailConfirmationModal}
                className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-4 rounded-lg font-bold text-lg hover:from-amber-700 hover:to-orange-700 transition shadow-lg"
              >
                {t.modals.emailConfirmButton}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Success Modal */}
      {showContactSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={closeContactSuccessModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={closeContactSuccessModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.modals.contactSuccessTitle}</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                {t.modals.contactSuccessText}
              </p>
              
              <button
                onClick={closeContactSuccessModal}
                className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-4 rounded-lg font-bold text-lg hover:from-amber-700 hover:to-orange-700 transition shadow-lg"
              >
                {t.modals.contactSuccessButton}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Music className="w-6 h-6 text-amber-500" />
                <span className="text-white font-bold">Intentional Worship</span>
              </div>
              <p className="text-sm">
                {t.footer.tagline}
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t.footer.booksTitle}</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={handleEnglishEditionsClick} className="hover:text-white transition">{t.footer.fearlessWorship}</button></li>
                <li><button onClick={handleEnglishEditionsClick} className="hover:text-white transition">{t.footer.intentionalWorship}</button></li>
                <li><button onClick={handleSpanishEditionsClick} className="hover:text-white transition">{t.footer.spanishEditions}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t.footer.resourcesTitle}</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={openAssessmentModal} className="hover:text-white transition">{t.footer.freeAssessment}</button></li>
                <li><button onClick={() => scrollToSection('resources')} className="hover:text-white transition">{t.footer.freeResources}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t.footer.contactTitle}</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition">{t.footer.getInTouch}</button></li>
                <li><a href="mailto:info@intentionalworshiplife.com" className="hover:text-white transition">{t.footer.emailUs}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>{t.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}